import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {SharedImports} from '@shared/shared-imports';
import {takeUntil} from 'rxjs/operators';
import {MessageService} from 'primeng/api';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {ContextModel} from '@app/models/context.model';
import {EnvironmentsService} from '@services/environments.service';
import {ContextService} from '@services/context.service';
import {UserAccountService} from '@services/user-account.service';
import {ConfirmValidationUtility} from '@shared/utilities/confirm-validation.utility';
import {Title} from '@angular/platform-browser';

@Component({
  templateUrl: './email-reset-password.component.html',
  styleUrls: ['./email-reset-password.component.scss'],
  imports: [SharedImports]
})
export class EmailResetPasswordComponent implements OnInit, OnDestroy {
  rpForm: FormGroup;
  tenant: string;
  logo: string;
  email: string;
  // qpusername: string;
  // qptoken: string;
  passwordType = 'password';
  passwordConfirmType = 'password';
  isLoading: boolean = false;
  requirements: boolean = false;
  // securityQuestionID: number;
  // securityQuestion: string;
  // tries: number = 0;

  private ngUnsubscribe = new Subject();

  constructor(private router: Router, private route: ActivatedRoute,
              private messageService: MessageService, private formBuilder: FormBuilder,
              private environmentsService: EnvironmentsService, private userAccountService: UserAccountService,
              private contextService: ContextService, private confirmValidationUtility: ConfirmValidationUtility,
              private titleService: Title) {
    this.rpForm = this.formBuilder.group({
      password: new FormControl(null, [Validators.required,
        this.regexValidator(new RegExp('^.*[A-Za-z0-9$@#!%*?&].{9,}'), {length: true}),
        this.regexValidator(new RegExp('^.*[a-z]'), {lowercase: true}),
        this.regexValidator(new RegExp('^.*[A-Z]'), {uppercase: true}),
        this.regexValidator(new RegExp('^.*[0-9]'), {number: true}),
        this.regexValidator(new RegExp('^.*[$@#!%*?&]'), {special: true}),
        this.regexValidator(new RegExp('^(?!.*([a-zA-Z0-9$@#!%*?&])\\1{2,})'), {repeats: true})
      ]),
      // password: new FormControl(null, [Validators.required, Validators.maxLength(100),
      //   Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])(?!.*([a-zA-Z0-9])\\1{2,})[A-Za-zd$@$!%*?&].{9,}')]),
      showPassword: new FormControl(false),
      passwordConfirm: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
      showPasswordConfirm: new FormControl(false)
    }, {
      validator: [this.confirmValidationUtility.ConfirmedValidator('password', 'passwordConfirm'),
        this.customValidator('password')]
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe({
      next: (params) => {
        // get the query parameters
        if (params && params.email) {
          this.email = params.email;
        }
        if (params && params.jwt) {
          sessionStorage.setItem('registrationToken', params.jwt);
        }
        this.logo = this.contextService.contextObject.tenantLogo;
      }
    });
    this.getBaseUrl().then(() => {

    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }


  regexValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }

  getBaseUrl() {
    return new Promise((resolve, reject) => {
      switch (true) {
        case environment.environmentName === 'dev':
        case environment.environmentName === 'staging':
        case environment.environmentName === 'demo':
          this.environmentsService.getEnvironment(environment.environmentName).then((rtnTenant) => {
            if (rtnTenant) {
              this.setUrl(rtnTenant);
            }
          });
          break;
        case environment.local
        && environment.environmentName !== 'dev'
        && environment.environmentName !== 'demo'
        && environment.environmentName !== 'staging':
          if (this.tenant) {
            this.environmentsService.getEnvironment(`${this.tenant.toLowerCase()}-${environment.environmentName}`).then((rtnTenant) => {
              if (rtnTenant) {
                this.setUrl(rtnTenant);
              }
            });
          }
          break;
        case !environment.local:
          this.environmentsService.getEnvironment(window.location.hostname).then((rtnTenant) => {
            if (rtnTenant) {
              this.setUrl(rtnTenant);
            }
          });
          break;
      }
      resolve(null);
    });
  }

  setUrl(rtnTenant) {
    const context = {} as ContextModel;
    context.apiBaseUrlV1 = rtnTenant.apiBaseUrlV1;
    context.apiBaseUrlV2 = rtnTenant.apiBaseUrlV2;
    context.tenantName = rtnTenant.tenantEnum;
    context.tenantLogo = rtnTenant.tenantLogo;
    context.accessToken = null;
    context.multiTenant = rtnTenant.multiTenant;

    this.titleService.setTitle(rtnTenant.pageTitle);
    sessionStorage.setItem('pageTitle', rtnTenant.pageTitle);
    this.contextService.contextObject = context;
    this.logo = this.contextService.contextObject.tenantLogo;
  }

  toggleRequirements() {
    this.requirements = this.requirements !== true;
  }


  resetPassword() {
    this.isLoading = true;
    const data = {
      EmailAddress: this.email,
      Password: this.rpForm.get('password').value,
    };
    this.userAccountService.passwordReset(data)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (msg) => {
          this.messageService.add({severity: 'success', summary: 'Success', detail: msg, life: 3000});
          setTimeout(() => {
            this.router.navigate(['login']);
          }, 3000);
        }, error: (e) => {
          if (e?.error?.detail) {
            this.messageService.add({severity: 'error', summary: 'Failure', detail: e.error.detail, life: 4500});
          } else {
            this.messageService.add({severity: 'error', summary: 'Failure', detail: 'Sorry an error occurred. Please contact your administrator', life: 4500});
          }
        }
      });
  }

  customValidator(pswdControlName: string) {
    return (formGroup: FormGroup) => {
      const matchingControl = formGroup.controls[pswdControlName];
      if (matchingControl.errors && !matchingControl.errors?.customValidator) {
        return;
      }
      const control: string = this.contextService.contextObject.UserName;
      if (control) {
        const am: number = control.indexOf('@');
        const username: string = control.substring(0, am);
        const domain: string = control.substring(am + 1, control.length);
        if (matchingControl.value.indexOf(username) > -1 || matchingControl.value.indexOf(domain) > -1) {
          matchingControl.setErrors({customValidator: true});
        } else {
          matchingControl.setErrors(null);
        }
      } else {
        matchingControl.setErrors(null);
      }
    };
  }
}
